import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/scroll";
import { HERO_IMAGE, EASE } from "@/lib/site";

const LINES = ["Control & optimisation", "for a renewable-heavy grid."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen min-h-[640px] overflow-hidden"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-24">
        <img
          src={HERO_IMAGE}
          alt="Aerial view of a utility-scale solar plant"
          className="w-full h-full object-cover animate-kenburns"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#F5F4F0]/55" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#F5F4F0] to-transparent" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col justify-end pb-40 sm:pb-48"
      >
        <motion.div
          data-testid="hero-tech-tags"
          className="flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] sm:text-xs uppercase tracking-[0.24em] text-[#121413]"
        >
          {["Physics-based models", "Digital twin", "Real-time control"].map((tag, i) => (
            <motion.span
              key={tag}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.25 + i * 0.13 }}
              className="flex items-center gap-5"
            >
              {i > 0 && <span className="w-1 h-1 rotate-45 bg-[#B86A28]" aria-hidden="true" />}
              <span className="font-semibold [text-shadow:0_1px_8px_rgba(245,244,240,0.9)]">{tag}</span>
            </motion.span>
          ))}
        </motion.div>
        <h1
          data-testid="hero-headline-title"
          className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-medium tracking-tight leading-[1.05] text-[#121413]"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.35 + i * 0.15 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 1 }}
          className="mt-8"
        >
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollToId("#contact")}
            className="inline-flex items-center gap-3 bg-[#2C4A3A] text-[#FAF9F6] px-7 py-3.5 text-sm font-medium hover:bg-[#121413] transition-colors duration-300 group"
          >
            Get in touch
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="absolute bottom-0 inset-x-0 z-10 border-t border-[#121413]/10"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-4 hidden sm:flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A4D4A]">
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[#2C4A3A]" /> Grid-side + behind-the-meter
          </span>
          <span data-testid="hero-telemetry-horizon">Control horizon: sub-second → multi-day</span>
          <span>Asset classes: solar · wind · storage</span>
        </div>
      </motion.div>
    </section>
  );
};
